import React, { useEffect, useRef } from "react";
import type { TimetableSession } from "../../lib/api";
import { dayHeadingInTz, resolveCategory, timeInTz } from "./lib";

type RowProps = {
  session: TimetableSession;
  tz: string;
  locationName?: string;
  highlighted: boolean;
  showDate: boolean;
  onBook: (s: TimetableSession) => void;
};

export function SessionRow({ session, tz, locationName, highlighted, showDate, onBook }: RowProps) {
  const cat = resolveCategory(session.course);
  const soldOut = session.availability <= 0;
  const fewSpots = !soldOut && session.availability <= 3;
  // Only build the formatter/format when the date is actually rendered.
  const shortDate = showDate
    ? new Intl.DateTimeFormat("en-GB", {
        timeZone: tz,
        weekday: "short",
        day: "numeric",
      }).format(new Date(session.startDateTime))
    : undefined;

  return (
    <div className={`bwo-row${highlighted ? " bwo-row--highlight" : ""}`} data-session-id={session._id}>
      {showDate && <div className="bwo-row-date">{shortDate}</div>}
      <div className="bwo-row-main">
        <div className="bwo-row-cat">
          <span className="bwo-dot" style={{ background: cat.dot }} />
          {cat.label}
        </div>
        <div className="bwo-row-title">{session.title || session.course?.title || "Event"}</div>
        <div className="bwo-row-meta">
          {timeInTz(session.startDateTime, tz)} – {timeInTz(session.endDateTime, tz)}
          {locationName ? ` · ${locationName}` : ""}
          {fewSpots && <span className="bwo-spots">{session.availability} spot{session.availability === 1 ? "" : "s"} left</span>}
        </div>
      </div>
      {soldOut ? (
        <span className="bwo-soldout-label">Sold out</span>
      ) : (
        <button type="button" className="bwo-book" onClick={() => onBook(session)}>
          Book Now
        </button>
      )}
    </div>
  );
}

type Props = {
  days: Map<string, TimetableSession[]>;
  tz: string;
  locationsById: Map<string, string>;
  highlightSessionId?: string;
  scrollDayKey?: string;
  onBook: (s: TimetableSession) => void;
};

export function ListView({ days, tz, locationsById, highlightSessionId, scrollDayKey, onBook }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const target = highlightSessionId
      ? root.querySelector(`[data-session-id="${highlightSessionId}"]`)
      : scrollDayKey
        ? root.querySelector(`[data-day-key="${scrollDayKey}"]`)
        : null;
    target?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightSessionId, scrollDayKey]);

  return (
    <div className="bwo-list" ref={containerRef}>
      {[...days.entries()].map(([dayKey, sessions]) => (
        <div key={dayKey} data-day-key={dayKey}>
          <h4 className="bwo-day-heading">{dayHeadingInTz(sessions[0].startDateTime, tz)}</h4>
          {sessions.map((s) => (
            <SessionRow
              key={s._id}
              session={s}
              tz={tz}
              locationName={s.locationId ? locationsById.get(s.locationId) : undefined}
              highlighted={s._id === highlightSessionId}
              showDate={false}
              onBook={onBook}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
